<script setup>
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import Modal from '@/components/modals/Modal.vue';
import BuyBenchmarkCreditsPanel from '@/components/modals/BuyBenchmarkCreditsPanel.vue';
import { useToast } from '@/composables/use-toast';
import { getFriendlyErrorMessage } from '@/utils/errorUtils.js';
import { getApiCreditsBalance } from '@/services/apiCreditsService';
import { createModelRun, estimateModelRun, getPublicLlms } from '@/services/modelRunService';

const props = defineProps({
  isOpen: { type: Boolean, default: false },
  datasetId: { type: [Number, String], default: null },
  version: { type: [Number, String], default: null },
  datasetName: { type: String, default: '' },
});

const emit = defineEmits(['close', 'created']);

const router = useRouter();
const { addToast } = useToast();

const models = ref([]);
const selectedIds = ref([]);
const estimate = ref(null);
const balance = ref(null);
const loadingModels = ref(false);
const catalogError = ref('');
const estimating = ref(false);
const submitting = ref(false);

const canEstimate = computed(
  () => props.datasetId && selectedIds.value.length > 0 && !estimating.value,
);

const canSubmit = computed(
  () =>
    estimate.value &&
    balance.value &&
    Number(balance.value.balance_micro) >= Number(estimate.value.hold_micro) &&
    !submitting.value,
);

const needsMoreCredits = computed(
  () =>
    Boolean(estimate.value) &&
    Boolean(balance.value) &&
    Number(balance.value.balance_micro) < Number(estimate.value.hold_micro),
);

const reset = () => {
  selectedIds.value = [];
  estimate.value = null;
  catalogError.value = '';
};

const loadCatalog = async () => {
  loadingModels.value = true;
  catalogError.value = '';
  try {
    const [llms, bal] = await Promise.all([getPublicLlms(), getApiCreditsBalance()]);
    models.value = Array.isArray(llms) ? llms : [];
    balance.value = bal;
    if (!models.value.length) {
      catalogError.value = 'No self-serve models available yet.';
    }
  } catch (error) {
    models.value = [];
    catalogError.value = getFriendlyErrorMessage(error, 'Failed to load models. Please try again.');
    addToast(catalogError.value, 'error');
  } finally {
    loadingModels.value = false;
  }
};

watch(
  () => props.isOpen,
  async (open) => {
    if (open) {
      reset();
      await loadCatalog();
    }
  },
);

const toggleModel = (id) => {
  const numId = Number(id);
  if (selectedIds.value.includes(numId)) {
    selectedIds.value = selectedIds.value.filter((x) => x !== numId);
  } else {
    selectedIds.value = [...selectedIds.value, numId];
  }
  estimate.value = null;
};

const runEstimate = async () => {
  if (!canEstimate.value) return;
  estimating.value = true;
  try {
    estimate.value = await estimateModelRun({
      dataset_id: Number(props.datasetId),
      version: props.version ? Number(props.version) : undefined,
      llm_ids: selectedIds.value,
    });
    balance.value = await getApiCreditsBalance();
  } catch (error) {
    addToast(getFriendlyErrorMessage(error, 'Failed to estimate cost. Please try again.'), 'error');
  } finally {
    estimating.value = false;
  }
};

const confirmRun = async () => {
  if (!canSubmit.value) return;
  submitting.value = true;
  try {
    const run = await createModelRun({
      dataset_id: Number(props.datasetId),
      version: props.version ? Number(props.version) : undefined,
      llm_ids: selectedIds.value,
    });
    addToast('Model run queued', 'success');
    emit('created', run);
    emit('close');
    router.push({ path: '/creator-profile', query: { page: 'eval-results' } });
  } catch (error) {
    const message = getFriendlyErrorMessage(error, 'Failed to start model run. Please try again.');
    addToast(message, 'error');
  } finally {
    submitting.value = false;
  }
};

const goToWallet = () => {
  emit('close');
  router.push({ path: '/creator-profile', query: { page: 'api-credits' } });
};

const onCreditsPurchased = async (payload) => {
  if (payload?.balance) {
    balance.value = payload.balance;
    return;
  }
  try {
    balance.value = await getApiCreditsBalance();
  } catch {
    // keep current balance
  }
};

const formatCredits = (micro) => {
  if (micro == null) return '—';
  return (Number(micro) / 1_000_000).toFixed(4);
};
</script>

<template>
  <Modal :isOpen="isOpen" title="Run my benchmark" size="xl" @close="emit('close')">
    <div class="flex flex-col gap-4 text-sm text-echoes-text">
      <p class="text-echoes-text/80">
        Run curated models against
        <span class="font-medium">{{ datasetName || `dataset #${datasetId}` }}</span>
        using Benchmark credits (separate from marketplace credits).
      </p>

      <div v-if="loadingModels" class="py-6 text-center text-echoes-text/60">
        <i class="mdi mdi-loading mdi-spin mr-1"></i>
        Loading models…
      </div>

      <div v-else class="flex flex-col gap-2 max-h-56 overflow-y-auto border border-echoes-border rounded-lg p-3">
        <label
          v-for="model in models"
          :key="model.id"
          class="flex items-start gap-3 cursor-pointer py-1"
        >
          <input
            type="checkbox"
            class="mt-1"
            :checked="selectedIds.includes(model.id)"
            @change="toggleModel(model.id)"
          />
          <span class="flex-1">
            <span class="font-medium">{{ model.hostname }}/{{ model.name }}</span>
            <span class="block text-xs text-echoes-text/60">
              prompt ${{ model.prompt_price_per_1m ?? '—' }}/1M · completion ${{ model.completion_price_per_1m ?? '—' }}/1M
            </span>
          </span>
        </label>
        <div v-if="!models.length" class="flex flex-col items-center gap-2 py-4 text-center text-echoes-text/60">
          <p>{{ catalogError || 'No self-serve models available yet.' }}</p>
          <button
            type="button"
            class="inline-flex items-center px-3 py-1.5 rounded-lg border border-echoes-border text-echoes-text text-xs disabled:opacity-50"
            data-testid="run-benchmark-retry-catalog"
            :disabled="loadingModels"
            @click="loadCatalog"
          >
            <i class="mdi mdi-refresh mr-1"></i>
            Try again
          </button>
        </div>
      </div>

      <div class="flex flex-wrap items-center gap-3">
        <button
          type="button"
          class="inline-flex items-center px-4 py-2 rounded-lg bg-echoes-primary text-white disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="!canEstimate"
          @click="runEstimate"
        >
          <i v-if="estimating" class="mdi mdi-loading mdi-spin mr-1"></i>
          {{ estimating ? 'Estimating…' : 'Estimate cost' }}
        </button>
        <span class="text-xs text-echoes-text/70">
          Balance: {{ formatCredits(balance?.balance_micro) }} credits
        </span>
      </div>

      <div
        class="rounded-lg border p-3 space-y-3"
        :class="needsMoreCredits ? 'border-echoes-primary/50 bg-echoes-primary/5' : 'border-echoes-border'"
      >
        <div class="flex items-center justify-between gap-2">
          <p class="text-sm font-semibold">Buy Benchmark credits</p>
          <button type="button" class="text-xs underline text-echoes-primary" @click="goToWallet">
            Open wallet
          </button>
        </div>
        <BuyBenchmarkCreditsPanel
          v-if="isOpen"
          compact
          :highlighted="needsMoreCredits"
          element-id="run-benchmark-card-element"
          @payment-success="onCreditsPurchased"
        />
      </div>

      <div v-if="estimate" class="rounded-lg bg-echoes-surface border border-echoes-border p-3 space-y-1">
        <p>Estimated: <strong>{{ formatCredits(estimate.estimated_micro) }}</strong> credits</p>
        <p>Hold (reserve): <strong>{{ formatCredits(estimate.hold_micro) }}</strong> credits</p>
        <p class="text-xs text-echoes-text/60">
          Tasks: {{ estimate.tasks_count }} · ~{{ estimate.tokens_per_task }} tokens/task · markup
          {{ estimate.markup_percent }}%
        </p>
        <p
          v-if="Number(balance?.balance_micro) < Number(estimate.hold_micro)"
          class="text-red-600 text-xs"
        >
          Insufficient balance for hold. Buy Benchmark credits to continue.
        </p>
      </div>

      <div class="flex justify-end gap-2 pt-2">
        <button type="button" class="px-4 py-2 rounded-lg border border-echoes-border" @click="emit('close')">
          Cancel
        </button>
        <button
          type="button"
          class="inline-flex items-center px-4 py-2 rounded-lg bg-echoes-primary text-white disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="!canSubmit"
          @click="confirmRun"
        >
          <i v-if="submitting" class="mdi mdi-loading mdi-spin mr-1"></i>
          {{ submitting ? 'Starting…' : 'Confirm & run' }}
        </button>
      </div>
    </div>
  </Modal>
</template>
